import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatearCLP, formatearPorcentajeSimple } from '@lexicore/shared';
import {
  IconoAlerta,
  IconoBasura,
  IconoBuscar,
  IconoCerrar,
  IconoCheck,
  IconoDescargar,
  IconoEmpresa,
  IconoMas,
} from '../componentes/Iconos';
import { Cargando, ErrorCarga, Panel, TarjetaKpi, Vacio } from '../componentes/UI';
import { useAvisos } from '../contextos/AvisosContext';
import { descargar } from '../lib/api';
import {
  useCategoriasGastoConfig,
  useConsolaCostos,
  useGastos,
  type Gasto,
} from '../lib/consultas';
import { useEjecutarRecurrentesMasivo, useEliminarGasto } from '../lib/mutaciones';
import { ModalPagarGasto } from './costos/ModalPagarGasto';
import { ModalRegistrarGasto } from './costos/ModalRegistrarGasto';

export function Costos() {
  const navegar = useNavigate();
  const avisos = useAvisos();

  // Filtros interactivos
  const [ambito, setAmbito] = useState<'TODOS' | 'PROYECTO' | 'GENERAL'>('TODOS');
  const [soloPendientes, setSoloPendientes] = useState<boolean>(false);
  const [busqueda, setBusqueda] = useState<string>('');
  const [categoriaGastoId, setCategoriaGastoId] = useState<string>('TODAS');
  const [estadoPago, setEstadoPago] = useState<'TODOS' | 'PENDIENTE' | 'PAGADO'>('TODOS');

  // Modales
  const [modalCrearAbierto, setModalCrearAbierto] = useState(false);
  const [gastoParaPagar, setGastoParaPagar] = useState<Gasto | null>(null);

  // Mutaciones
  const eliminarMut = useEliminarGasto();
  const ejecutarRecurrentesMut = useEjecutarRecurrentesMasivo();

  // Consultas backend
  const consolaQuery = useConsolaCostos();
  const categoriasQuery = useCategoriasGastoConfig(false);

  // Query con parámetros reactivos
  const gastosQuery = useGastos({
    ambito: ambito !== 'TODOS' ? ambito : undefined,
    soloPendientes: soloPendientes || undefined,
    categoriaGastoId: categoriaGastoId !== 'TODAS' ? categoriaGastoId : undefined,
    estadoPago: estadoPago !== 'TODOS' ? estadoPago : undefined,
    busqueda: busqueda.trim() || undefined,
    limite: 100,
  });

  const consola = consolaQuery.data;
  const gastosData = gastosQuery.data?.datos ?? [];
  const totales = gastosQuery.data?.totales;

  const hayFiltrosActivos =
    ambito !== 'TODOS' ||
    soloPendientes ||
    busqueda.trim() !== '' ||
    categoriaGastoId !== 'TODAS' ||
    estadoPago !== 'TODOS';

  const limpiarFiltros = () => {
    setAmbito('TODOS');
    setSoloPendientes(false);
    setBusqueda('');
    setCategoriaGastoId('TODAS');
    setEstadoPago('TODOS');
  };

  const handleEjecutarRecurrentes = async () => {
    try {
      const res = await ejecutarRecurrentesMut.mutateAsync();
      avisos.exito(res.mensaje || `Se generaron ${res.generados} gastos recurrentes para este periodo.`);
      void consolaQuery.refetch();
      void gastosQuery.refetch();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al ejecutar gastos recurrentes.');
    }
  };

  const handleEliminarGasto = async (g: Gasto) => {
    if (!window.confirm(`¿Estás seguro de eliminar el registro de gasto "${g.concepto}" por ${formatearCLP(g.montoTotal || g.montoNeto)}?`)) {
      return;
    }
    try {
      await eliminarMut.mutateAsync(g.id);
      avisos.exito('Gasto eliminado correctamente.');
      void consolaQuery.refetch();
      void gastosQuery.refetch();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo eliminar el gasto.');
    }
  };

  if (consolaQuery.isLoading && !consola) return <Cargando texto="Cargando radar de costos y egresos…" />;
  if (consolaQuery.isError) return <ErrorCarga error={consolaQuery.error} reintentar={() => void consolaQuery.refetch()} />;

  const kpis = consola?.kpis;
  const proyectosEnRiesgo = consola?.proyectosEnRiesgo ?? [];
  const distribucion = consola?.distribucionPorCategoria ?? [];

  return (
    <>
      {/* ── Cabecera Principal ── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.01em' }}>
            Control de Costos & Egresos
          </h1>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
            Radar de egresos y salud de caja: control de costos directos de proyectos, gastos de estructura y cuentas por pagar a proveedores.
          </p>
        </div>

        {/* Acciones Rápidas de Cabecera */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => void descargar('/gastos/exportar', 'reporte_gastos.csv')}
            title="Exportar archivo CSV con formato UTF-8 para Excel y Formulario F29 del SII"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <IconoDescargar size={13} />
            <span>Exportar CSV (F29 / Excel)</span>
          </button>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => void handleEjecutarRecurrentes()}
            disabled={ejecutarRecurrentesMut.isPending}
            title="Generar masivamente los gastos fijos del mes vencidos a la fecha"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <span>⚡ Ejecutar Recurrentes</span>
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setModalCrearAbierto(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}
          >
            <IconoMas size={15} />
            <span>+ Registrar Gasto</span>
          </button>
        </div>
      </div>

      {/* ── Radar Directivo de Egresos: 5 KPIs Ejecutivos ── */}
      {kpis && (
        <div className="kpi-grid" style={{ marginBottom: 20 }}>
          <TarjetaKpi
            etiqueta="Presupuesto Comprometido"
            valor={formatearCLP(kpis.presupuestoTotalComprometido)}
            acento="var(--accent-gray)"
            nota={`${kpis.proyectosTotalCount} proyecto(s) con plan`}
          />
          <TarjetaKpi
            etiqueta="Gasto Real Total"
            valor={formatearCLP(kpis.gastoRealTotal)}
            acento="var(--accent-blue)"
            colorValor="var(--accent-blue)"
            nota={`${formatearPorcentajeSimple(kpis.consumoPresupuestoPct, 1)} consumido · Desviación: ${formatearCLP(kpis.desviacionGlobalNeto)}`}
          />
          <TarjetaKpi
            etiqueta="Cuentas por Pagar"
            valor={formatearCLP(kpis.cuentasPorPagarNeto)}
            acento={kpis.cuentasPorPagarCount > 0 ? 'var(--accent-red)' : 'var(--accent-green)'}
            colorValor={kpis.cuentasPorPagarCount > 0 ? 'var(--accent-red)' : 'var(--accent-green)'}
            nota={`${kpis.cuentasPorPagarCount} factura(s) pendiente(s) de pago`}
          />
          <TarjetaKpi
            etiqueta="Gastos Generales (Mes)"
            valor={formatearCLP(kpis.gastosGeneralesMesNeto)}
            acento="var(--accent-purple)"
            colorValor="var(--accent-purple)"
            nota="Estructura fija empresarial"
          />
          <TarjetaKpi
            etiqueta="Salud de Costos"
            valor={
              kpis.proyectosEnRiesgoCount === 0 ? (
                <span style={{ color: 'var(--accent-green)' }}>100% Sanos</span>
              ) : (
                <span style={{ color: 'var(--accent-red)' }}>
                  {kpis.proyectosEnRiesgoCount} en riesgo
                </span>
              )
            }
            acento={kpis.proyectosEnRiesgoCount > 0 ? 'var(--accent-red)' : 'var(--accent-green)'}
            nota={
              kpis.proyectosEnRiesgoCount > 0
                ? 'Requieren revisión inmediata'
                : 'Todos los proyectos dentro del plan'
            }
          />
        </div>
      )}

      {/* ── Semáforo Preventivo de Proyectos en Riesgo de Sobrecosto ── */}
      {proyectosEnRiesgo.length > 0 && (
        <Panel
          titulo={`⚠️ Proyectos con Alerta de Sobrecosto (${proyectosEnRiesgo.length})`}
          subtitulo="Proyectos que consumieron más del 80% del presupuesto planificado o superaron el límite"
          style={{ marginBottom: 20, borderColor: 'rgba(239, 68, 68, 0.4)' }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
            {proyectosEnRiesgo.map((p) => {
              const esExcedido = p.estado === 'EXCEDIDO' || p.consumoPct >= 100;
              const colorAcento = esExcedido ? 'var(--accent-red)' : 'var(--accent-yellow)';

              return (
                <div
                  key={p.id}
                  onClick={() => navegar(`/proyectos/${p.id}`)}
                  style={{
                    background: 'var(--bg-card)',
                    border: `1px solid ${colorAcento}`,
                    borderRadius: 'var(--radius-md)',
                    padding: '14px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{p.nombre}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                        {p.codigo} {p.organismoNombre && `· ${p.organismoNombre}`}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '3px 8px',
                        borderRadius: 'var(--radius-sm)',
                        background: esExcedido ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                        color: colorAcento,
                      }}
                    >
                      {formatearPorcentajeSimple(p.consumoPct)}
                    </span>
                  </div>

                  {/* Barra de Consumo */}
                  <div className="budget-track">
                    <div
                      className={`budget-fill ${esExcedido ? 'excedido' : 'aviso'}`}
                      style={{ width: `${Math.min(100, p.consumoPct)}%` }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
                    <span style={{ color: 'var(--text-muted)' }}>
                      Plan: <strong style={{ color: 'var(--text-secondary)' }}>{formatearCLP(p.presupuestoNeto)}</strong>
                    </span>
                    <span style={{ color: 'var(--text-muted)' }}>
                      Gastado: <strong style={{ color: colorAcento }}>{formatearCLP(p.costoRealNeto)}</strong>
                    </span>
                  </div>

                  <div style={{ fontSize: 11, fontWeight: 600, color: colorAcento, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IconoAlerta size={12} />
                    <span>{p.mensajeAlerta}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* ── Distribución de Egresos: Proporción de Oro (Costos Directos vs Indirectos) ── */}
      {distribucion.length > 0 && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: '14px 18px',
            marginBottom: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>
              Distribución de Costos Directos vs Gastos Generales
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              Principio de Oro: Costos Directos generan valor; Gastos Indirectos deben controlarse.
            </span>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {distribucion.map((d) => {
              const esDirecto = d.tipo === 'DIRECTO';
              return (
                <div
                  key={d.categoriaId}
                  style={{
                    flex: '1 1 200px',
                    padding: '10px 14px',
                    borderRadius: 'var(--radius-sm)',
                    background: esDirecto ? 'rgba(59, 130, 246, 0.08)' : 'rgba(168, 85, 247, 0.08)',
                    border: `1px solid ${esDirecto ? 'rgba(59, 130, 246, 0.2)' : 'rgba(168, 85, 247, 0.2)'}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>
                      {d.categoriaNombre}
                    </span>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: esDirecto ? 'var(--accent-blue)' : 'var(--accent-purple)',
                      }}
                    >
                      {formatearPorcentajeSimple(d.porcentajeDelTotal)}
                    </span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                    {formatearCLP(d.montoNeto)}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    {esDirecto ? '🎯 Imputable a Proyecto' : '🏢 Estructura de Empresa'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Barra de Filtros Instantáneos 1-Clic (100% Operativa) ── */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 16px',
          marginBottom: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* Fila 1: Pestañas de 1 Clic (Ámbito y Cuentas por Pagar) */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            className={`btn btn-sm ${ambito === 'TODOS' && !soloPendientes ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setAmbito('TODOS');
              setSoloPendientes(false);
            }}
            style={{ fontSize: 12, padding: '6px 14px' }}
          >
            Todos los egresos
          </button>

          <button
            type="button"
            className={`btn btn-sm ${ambito === 'PROYECTO' && !soloPendientes ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setAmbito('PROYECTO');
              setSoloPendientes(false);
            }}
            style={{ fontSize: 12, padding: '6px 14px' }}
          >
            🎯 Solo Costos de Proyecto (CD)
          </button>

          <button
            type="button"
            className={`btn btn-sm ${ambito === 'GENERAL' && !soloPendientes ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setAmbito('GENERAL');
              setSoloPendientes(false);
            }}
            style={{ fontSize: 12, padding: '6px 14px' }}
          >
            🏢 Solo Gastos Generales (GI)
          </button>

          <button
            type="button"
            className={`btn btn-sm ${soloPendientes ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSoloPendientes((prev) => !prev)}
            style={{
              fontSize: 12,
              padding: '6px 14px',
              borderColor: soloPendientes ? 'var(--accent-red)' : 'rgba(239, 68, 68, 0.4)',
              color: soloPendientes ? '#fff' : 'var(--accent-red)',
              background: soloPendientes ? 'var(--accent-red)' : 'transparent',
              fontWeight: 600,
            }}
          >
            🔴 Cuentas por Pagar (Pendientes)
          </button>

          <div style={{ flexGrow: 1 }} />

          {hayFiltrosActivos && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ fontSize: 11.5, padding: '5px 10px', color: 'var(--text-muted)' }}
            >
              <IconoCerrar size={12} />
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>

        {/* Fila 2: Buscador, Categoría y Estado */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Buscador predictivo */}
          <div style={{ position: 'relative', flex: '1 1 240px', display: 'flex', alignItems: 'center' }}>
            <span style={{ position: 'absolute', left: 12, color: 'var(--text-muted)', pointerEvents: 'none' }}>
              <IconoBuscar size={14} />
            </span>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por concepto, proveedor o folio…"
              style={{
                width: '100%',
                padding: '8px 34px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 13,
              }}
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                style={{
                  position: 'absolute',
                  right: 8,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label="Limpiar búsqueda"
              >
                <IconoCerrar size={12} />
              </button>
            )}
          </div>

          {/* Categoría */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Categoría:
            </span>
            <select
              value={categoriaGastoId}
              onChange={(e) => setCategoriaGastoId(e.target.value)}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            >
              <option value="TODAS">Todas las categorías</option>
              {(categoriasQuery.data ?? []).map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nombre} ({cat.tipo})
                </option>
              ))}
            </select>
          </div>

          {/* Estado de Pago */}
          {!soloPendientes && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
                Pago:
              </span>
              <select
                value={estadoPago}
                onChange={(e) => setEstadoPago(e.target.value as any)}
                style={{
                  padding: '7px 10px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-primary)',
                  fontSize: 12.5,
                }}
              >
                <option value="TODOS">Todos los estados</option>
                <option value="PENDIENTE">Solo Pendientes</option>
                <option value="PAGADO">Solo Pagados</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ── Libro Mayor de Gastos ── */}
      <div className="tabla-wrap">
        <div className="tabla-scroll">
          <table style={{ minWidth: 960 }}>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Ámbito & Destino</th>
                <th>Categoría</th>
                <th>Proveedor & Folio</th>
                <th>Concepto / Glosa</th>
                <th style={{ textAlign: 'right' }}>Monto Neto</th>
                <th style={{ textAlign: 'right' }}>Total a Pagar</th>
                <th style={{ textAlign: 'center' }}>Estado</th>
                <th style={{ textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {gastosQuery.isLoading && (
                <tr>
                  <td colSpan={9}>
                    <Cargando texto="Cargando libro de egresos…" />
                  </td>
                </tr>
              )}

              {!gastosQuery.isLoading && gastosData.length === 0 && (
                <tr>
                  <td colSpan={9}>
                    <Vacio
                      titulo="Sin gastos registrados con este filtro"
                      detalle="Puedes registrar un nuevo costo con el botón superior o cambiar los criterios de búsqueda."
                    />
                  </td>
                </tr>
              )}

              {gastosData.map((gasto) => {
                const esPendiente = gasto.estadoPago === 'PENDIENTE';
                const esProyecto = Boolean(gasto.proyecto);

                return (
                  <tr
                    key={gasto.id}
                    onClick={() => gasto.proyecto && navegar(`/proyectos/${gasto.proyecto.id}`)}
                    style={{ cursor: gasto.proyecto ? 'pointer' : 'default' }}
                  >
                    {/* Fecha */}
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                      {gasto.fecha.slice(0, 10)}
                    </td>

                    {/* Ámbito & Proyecto */}
                    <td>
                      {esProyecto && gasto.proyecto ? (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                            {gasto.proyecto.nombre}
                          </div>
                          <div style={{ fontSize: 10.5, color: 'var(--accent-blue)', fontFamily: 'var(--font-mono)' }}>
                            {gasto.proyecto.codigo}
                          </div>
                        </div>
                      ) : (
                        <span
                          style={{
                            fontSize: 11,
                            padding: '3px 8px',
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--accent-purple-soft)',
                            color: 'var(--accent-purple)',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <IconoEmpresa size={11} />
                          <span>Gasto General Empresa</span>
                        </span>
                      )}
                    </td>

                    {/* Categoría */}
                    <td>
                      <span
                        className="badge badge-cat"
                        style={{
                          background: gasto.categoriaGasto.tipo === 'DIRECTO' ? 'var(--accent-blue-soft)' : 'var(--accent-purple-soft)',
                          color: gasto.categoriaGasto.tipo === 'DIRECTO' ? 'var(--accent-blue)' : 'var(--accent-purple)',
                        }}
                      >
                        {gasto.categoriaGasto.nombre}
                      </span>
                    </td>

                    {/* Proveedor & Folio */}
                    <td>
                      <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>
                        {gasto.proveedor?.nombre ?? gasto.proveedorNombre ?? 'Sin proveedor'}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                        {gasto.numeroDocumento ? `Folio #${gasto.numeroDocumento}` : (gasto.tipoDocumento ?? 'Comprobante')}
                      </div>
                    </td>

                    {/* Concepto */}
                    <td style={{ fontSize: 12.5, color: 'var(--text-primary)', maxWidth: 220 }}>
                      {gasto.concepto}
                    </td>

                    {/* Monto Neto */}
                    <td className="td-mono" style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                      {formatearCLP(gasto.montoNeto)}
                    </td>

                    {/* Monto Total Bruto */}
                    <td className="td-mono" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {formatearCLP(gasto.montoTotal || gasto.montoNeto)}
                    </td>

                    {/* Estado de Pago */}
                    <td style={{ textAlign: 'center' }}>
                      <span className={`factura-badge ${esPendiente ? 'PENDIENTE' : 'PAGADA'}`}>
                        {esPendiente ? 'Pendiente' : 'Pagado'}
                      </span>
                    </td>

                    {/* Acciones */}
                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                        {esPendiente && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setGastoParaPagar(gasto)}
                            style={{
                              background: 'var(--accent-green)',
                              color: '#fff',
                              border: 'none',
                              padding: '4px 8px',
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                            title="Marcar este gasto como pagado"
                          >
                            <IconoCheck size={11} />
                            <span>Pagar</span>
                          </button>
                        )}

                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => void handleEliminarGasto(gasto)}
                          style={{ padding: '4px 7px', color: 'var(--accent-red)' }}
                          title="Eliminar registro"
                        >
                          <IconoBasura size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Barra de Totales y Conciliación al Pie de la Tabla */}
        {totales && (
          <div
            style={{
              padding: '12px 18px',
              background: 'var(--bg-elevated)',
              borderTop: '1px solid var(--border-subtle)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16,
              fontSize: 12,
            }}
          >
            <span style={{ color: 'var(--text-muted)' }}>
              Mostrando <strong>{gastosData.length}</strong> registro(s) filtrado(s)
            </span>

            <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>Neto Total: </span>
                <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                  {formatearCLP(totales.montoNeto)}
                </strong>
              </div>

              <div>
                <span style={{ color: 'var(--text-muted)' }}>IVA: </span>
                <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>
                  {formatearCLP(totales.montoIva ?? (totales.montoTotal - totales.montoNeto))}
                </strong>
              </div>

              <div>
                <span style={{ color: 'var(--text-muted)' }}>Bruto Total: </span>
                <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', fontSize: 13 }}>
                  {formatearCLP(totales.montoTotal)}
                </strong>
              </div>

              <div>
                <span style={{ color: 'var(--accent-red)' }}>Pendiente: </span>
                <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-red)' }}>
                  {formatearCLP(totales.montoPendienteNeto)}
                </strong>
              </div>

              <div>
                <span style={{ color: 'var(--accent-green)' }}>Pagado: </span>
                <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-green)' }}>
                  {formatearCLP(totales.montoPagadoNeto)}
                </strong>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal de Registro Rápido de Gasto ── */}
      {modalCrearAbierto && (
        <ModalRegistrarGasto
          alCerrar={() => setModalCrearAbierto(false)}
          alCrearExitoso={() => {
            void consolaQuery.refetch();
            void gastosQuery.refetch();
          }}
        />
      )}

      {/* ── Modal de Confirmación de Pago Rápido ── */}
      {gastoParaPagar && (
        <ModalPagarGasto
          gasto={gastoParaPagar}
          alCerrar={() => setGastoParaPagar(null)}
          alPagarExitoso={() => {
            void consolaQuery.refetch();
            void gastosQuery.refetch();
          }}
        />
      )}
    </>
  );
}
